import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import Button from "components/Button/Button";
import { Input } from "components/inputs/Input/Input";
import { TextArea } from "components/inputs/TextArea";
import LoadingScreen from "components/Loading/LoadingScreen";
import { useBeaconStore, useLayoutStore } from "contexts/RootStore.context";
import { Horizontal, Vertical } from "gls/lib";
import useDevice from "hooks/useDevice";
import { isEmpty } from "lodash";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { routePath } from "router/routePaths";
import { style } from "typestyle";
import { alertErrorMessage, alertSuccessMessage } from "utils/alerts";
import { RequiredValidationError } from "utils/formstate";
import ConfirmCancelModal from "./ConfirmCancelModal";
import { useBlocker } from "hooks/useBlocker";
import { MAX_CHARACTERS_IN_ASSET_DESCRIPTION, MAX_CHARACTERS_IN_ASSET_NAME } from "config/constants";
import { SeperatorStyle } from "../Styled";
import FormHeader from "components/FormHeader";
import { InputNumber } from "components/inputs/InputNumber/InputNumber";
import WidthNotSupportedPage from "views/WidthNotSupportedPage";
import { formStyle } from "styling/App.style";
import { Toggle } from "components/inputs/Toggle/Toggle";

export const BeaconForm = observer(() => {

  const { isMobileDevice } = useDevice();
  const { beaconForm, loadBeacon, reset, dataLoading, error,
    isAnyChange, ischangesSaved, isFormEmpty, SaveBeacon, saveLoading } = useBeaconStore()
  const params = useParams<{ beaconId: string | undefined }>();
  const editMode = !isEmpty(params.beaconId)
  const { setPageError } = useLayoutStore()
  const navigate = useNavigate()
  const [canelChangesModal, setCanelChangesModal] = useState(false)

  const { handleLeave, handleStay } = useBlocker(() => {
    if (!ischangesSaved && ((editMode && isAnyChange) || (!editMode && !isFormEmpty))) {
      setCanelChangesModal(true)
      return true
    }
    return false
  });

  useEffect(() => {
    const abortController = new AbortController();

    if (params.beaconId) {
      loadBeacon(params.beaconId)
        .finally(() => {
          if (abortController.signal.aborted) {
            reset();
          }
        })
    }
    return () => {
      abortController.abort();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPageError(error)
  }, [error, setPageError])

  if (isMobileDevice) {
    return <WidthNotSupportedPage />
  }

  const saveBeacon = () => {
    SaveBeacon()
      .then(() => {
        if (params.beaconId)
          alertSuccessMessage('Changes saved for the beacon')
        else
          alertSuccessMessage(`${beaconForm.$.beacon_id.value} has been added`)
        navigate(routePath.beaconsList)
      })
      .catch((error: ErrorResponseDto) => {
        if (error?.errorMsg)
          alertErrorMessage(error.errorMsg)
      })
  }

  console.log(beaconForm.error)

  if (dataLoading) {
    return <LoadingScreen />
  }
  return (
    <>
      <Vertical sizing="stretch" scroll="vertical" className={containerStyle} spacing={20}>
        <FormHeader text="BEACON DETAILS" bold />
        <Vertical className={formStyle} padding={30}>
          <Input
            label="Beacon ID:"
            required
            className={fieldStyle}
            value={beaconForm.$.beacon_id.value}
            onChange={beaconForm.$.beacon_id.onChange}
            disabled={editMode}
            id="beacon_id"
            name="beacon_id"
            maxLength={100}
            error={beaconForm.$.beacon_id.error !== RequiredValidationError ? beaconForm.$.beacon_id.error : ""}
          />
          <Horizontal>
            <Input
              label="Beacon Type:"
              className={fieldStyle}
              value={beaconForm.$.beacon_type.value}
              onChange={beaconForm.$.beacon_type.onChange}
              id="beacon_type"
              name="beacon_type"
              maxLength={MAX_CHARACTERS_IN_ASSET_NAME}
              showMaxLength
            />
            <Input
              label="MAC Address:"
              className={fieldStyle}
              value={beaconForm.$.mac_address.value}
              onChange={beaconForm.$.mac_address.onChange}
              id="mac_address"
              name="mac_address"
              maxLength={17}
              showMaxLength
              error={beaconForm.$.mac_address.error}
              disabled={!(beaconForm.$.hasEddystone.value || beaconForm.$.hasIbeacon.value)}
            />
          </Horizontal>
          <TextArea
            id="notes-input"
            label="Notes:"
            value={beaconForm.$.notes.value}
            onChange={beaconForm.$.notes.onChange}
            className={fieldStyle}
            maxLength={MAX_CHARACTERS_IN_ASSET_DESCRIPTION}
            maxWidth="723px"
          />
        </Vertical>
        <FormHeader text="Broadcast Profiles" bold />
        <Vertical className={formStyle} padding={30}>
          <Toggle
            id='eddystone-Broadcast-Profile'
            name='eddystone-Broadcast-Profile'
            value={beaconForm.$.hasEddystone.value}
            onChange={(value) => {
              beaconForm.$.hasEddystone.onChange(value)
              if (!value) {
                beaconForm.$.namespace.onChange("")
                beaconForm.$.instance.onChange("")
                //clear mac address on both disable Broadcast profiles
                if (!beaconForm.$.hasIbeacon.value) {
                  beaconForm.$.mac_address.onChange('')
                }
              }
              beaconForm.validate()
            }}
            label="Eddystone"
            labelPlace="right"
          />
          <Horizontal>
            <Input
              label="Namespace:"
              className={fieldStyle}
              disabled={!beaconForm.$.hasEddystone.value}
              required={beaconForm.$.hasEddystone.value}
              value={beaconForm.$.namespace.value}
              onChange={beaconForm.$.namespace.onChange}
              id="namespace"
              name="namespace"
            />
            <Input
              label="Instance:"
              className={fieldStyle}
              disabled={!beaconForm.$.hasEddystone.value}
              required={beaconForm.$.hasEddystone.value}
              value={beaconForm.$.instance.value}
              onChange={beaconForm.$.instance.onChange}
              id="instance"
              name="instance"
            />
          </Horizontal>
          <div className={SeperatorStyle} />
          <Toggle
            id='iBeacon-Broadcast-Profile'
            name='iBeacon-Broadcast-Profile'
            value={beaconForm.$.hasIbeacon.value}
            onChange={(value) => {
              beaconForm.$.hasIbeacon.onChange(value)
              if (!value) {
                beaconForm.$.proximity_uuid.onChange("")
                beaconForm.$.major.onChange(undefined)
                beaconForm.$.minor.onChange(undefined)
                //clear mac address on both disable Broadcast profiles
                if (!beaconForm.$.hasEddystone.value) {
                  beaconForm.$.mac_address.onChange('')
                }
              }
              beaconForm.validate()
            }}
            label="iBeacon"
            labelPlace="right"
          />
          <Input
            label="Proximity UUID:"
            className={fieldStyle}
            required={beaconForm.$.hasIbeacon.value}
            value={beaconForm.$.proximity_uuid.value}
            onChange={beaconForm.$.proximity_uuid.onChange}
            disabled={!beaconForm.$.hasIbeacon.value}
            id="proximity_uuid"
            name="proximity_uuid"
            error={beaconForm.$.proximity_uuid.error !== RequiredValidationError ? beaconForm.$.proximity_uuid.error : ""}
          />
          <Horizontal>
            <InputNumber
              label="Major:"
              value={beaconForm.$.major.value}
              onChange={beaconForm.$.major.onChange}
              disabled={!beaconForm.$.hasIbeacon.value}
              required={beaconForm.$.hasIbeacon.value}
              name="major"
              id="major"
              width="160px"
            />
            <InputNumber
              label="Minor:"
              value={beaconForm.$.minor.value}
              onChange={beaconForm.$.minor.onChange}
              disabled={!beaconForm.$.hasIbeacon.value}
              required={beaconForm.$.hasIbeacon.value}
              name="minor"
              id="minor"
              width="160px"
            />
          </Horizontal>
        </Vertical>
        <Horizontal horizontalAlign="right" spacing={10}>
          <Button className="biggerButton"
            isLoading={saveLoading}
            outline
            onClick={() => navigate(routePath.beaconsList)}
            id="cancel-utton">
            Cancel
          </Button>
          <Button className="biggerButton"
            onClick={saveBeacon}
            isLoading={saveLoading}
            id="save-button"
            disabled={!isAnyChange || beaconForm.hasError}
          >
            {editMode ? 'Save' : 'Add'}
          </Button>
        </Horizontal>
      </Vertical>

      {canelChangesModal &&
        <ConfirmCancelModal
          editMode={editMode}
          onClose={() => {
            setCanelChangesModal(false)
            handleStay()
          }}
          onCancel={() => {
            setCanelChangesModal(false)
            handleLeave();
          }}
          onConfirm={() => {
            setCanelChangesModal(false)
            handleStay()
            if (!beaconForm.hasError)
              saveBeacon()
          }}
        />
      }
    </>
  );
});

const containerStyle = style({
  marginBottom: '20px'
})

const fieldStyle = style({
  minWidth: "350px",
  width: "350px",
});
